import styled from 'styled-components';
import { easing, duration } from 'ui/theme';
import { Skeleton } from 'ui/skeleton';

import { MeterOptions, METER_CHART_VARIANTS } from './types';
import { useMeterFormatData, useDefaultColorPalette } from './hooks';
import { SIZE_STYLES } from './MeterSizes';
import { MeterSegment } from './MeterSegment';
import { MeterLegend } from './MeterLegend';
import { MeterTip } from './MeterTip';
import { CHART_SIZES } from '../types';

export type MeterProps = {
  /** options that will be displayed for the chart */
  options: MeterOptions[];
  /** boolean condition on whether data is loading */
  isLoading: boolean;
  /** selected options to show for the chart  */
  selectedOptions?: string[];
  /** sizes of the chart -- small, medium, large */
  size?: CHART_SIZES;
  /** meter specific variants -- default, comparison, related: blue, green, purple */
  variant?: METER_CHART_VARIANTS;
  /** whether the meter has a legend.
   * Legends should be used for Large size meter charts as per zero height
   * with one or two options
   * https://zeroheight.com/3fe730a39/v/0/p/17febe-charts/b/520783/t/67cf9e
   * */
  hasLegend?: boolean;
  /** boolean condition on if meter has tooltips */
  hasTooltip?: boolean;
  /** boolean condition on if meter has toggletips */
  hasToggletip?: boolean;
  /** boolean condition on if meter segments have highlight on hover */
  hasHighlight?: boolean;
  /** boolean condition on if meter has props: hasLegend, hasTooltip, and hasHiglight */
  isAccessible?: boolean;
};

const MeterContainer = styled.div<{ size: CHART_SIZES }>`
  ${({ size }) => SIZE_STYLES[size]};
  background: #f3f4f5;
  border-radius: ${({ theme }) => theme.spacing.values.eighth};
  column-gap: ${({ theme }) => theme.spacing.values.eighth};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin: ${({ theme }) => `
  15px 0 ${theme.spacing.values.half} 0
  `};
  overflow: hidden;
  width: 100%;
  transition: all ${duration.quick} ${easing.standard};

  :not(:last-child) {
    border-left: 1px solid #fff;
  }
`;

const useAccessibilityProps = (isAccessible: boolean) =>
  ['legend', 'tooltip', 'highlight'].reduce(
    (acc, prop) => ({
      ...acc,
      [prop]: isAccessible,
    }),
    {} as Required<{
      legend: MeterProps['hasLegend'];
      tooltip: MeterProps['hasTooltip'];
      highlight: MeterProps['hasHighlight'];
    }>,
  );

export const DATA_TEST = {
  container: 'meter-container',
};

export const Meter = ({
  options,
  isLoading,
  selectedOptions,
  size = CHART_SIZES.MEDIUM,
  variant = METER_CHART_VARIANTS.DEFAULT,
  isAccessible = true,
  hasTooltip = false,
  hasToggletip = false,
  hasHighlight = false,
  hasLegend = false,
}: MeterProps) => {
  const { meterBarData, totalValue } = useMeterFormatData(options, selectedOptions);
  const meterColors = useDefaultColorPalette(variant);
  const { tooltip, legend, highlight } = useAccessibilityProps(isAccessible);
  const hasMeterTip = isAccessible || hasTooltip || hasToggletip;

  return (
    <>
      <MeterContainer size={size} data-test={DATA_TEST.container}>
        {isLoading ? (
          <Skeleton />
        ) : (
          meterBarData.map(({ value, color, name, tooltipContent }, i) =>
            hasMeterTip ? (
              <MeterTip
                key={`meter-tooltip-${name}`}
                tooltipContent={tooltipContent ?? name}
                value={value}
                total={totalValue}
                hasTooltip={tooltip || hasTooltip}
              >
                <MeterSegment
                  key={`meter-bar-${name}`}
                  size={size}
                  value={value}
                  name={name}
                  total={totalValue}
                  color={color ?? meterColors[i]}
                  hasHighlight={highlight || hasHighlight}
                  isFullWidth
                />
              </MeterTip>
            ) : (
              <MeterSegment
                key={`meter-bar-${name}`}
                size={size}
                value={value}
                name={name}
                total={totalValue}
                color={color ?? meterColors[i]}
                hasHighlight={hasHighlight}
              />
            ),
          )
        )}
      </MeterContainer>
      <MeterLegend
        options={meterBarData}
        defaultColors={meterColors}
        isEnabled={legend || hasLegend}
        isLoading={isLoading}
      />
    </>
  );
};
